<script setup lang="ts">
    import { computed, onMounted, reactive, ref, toRaw } from 'vue';
    import axios from 'axios';
    import ConfirmationModal from '@/components/ConfirmationModal.vue';
    import { formatAmount, formatDate } from '@/utils';
    import { useRoute } from 'vue-router';
    
    const route = useRoute();
    const errorMessage = ref('');
    const loading = ref(false);
    const itemsList = ref([] as any[]);
    const selectedItem = ref(null as any);
    const confirmationModalRef = ref<InstanceType<typeof ConfirmationModal> | null>(null);
    const selectedItems = ref([] as any[]);
    const areAllItemsSelected = computed(() => {
        return itemsList.value.length == selectedItems.value.length;
    });
    const startupInfos = reactive({
        status_carte: {} as any,
        banques: [] as any[],
        lots: [] as any[],
    });
    const queryParams = reactive({
        pageNumber: 1,
        sortDir: 'ASC',
        sortColumn: 'numero',
        sortOption: 'numero_asc',
        borneInf: '',
        borneSup: '',
        banque_id: '',
        statut: '',
        num_lot: '',
        search: '',
        trashed: 0
    });
    const pagination = reactive({
        total: 0,
        per_page: 10,
        last_page: 1
    });
    const SORT_OPTIONS = [
        { value: 'numero_asc', label: 'Numero (ASC)', sort_column: 'numero', sort_direction: 'ASC' },
        { value: 'numero_desc', label: 'Numero (DESC)', sort_column: 'numero', sort_direction: 'DESC' },
        { value: 'created_at_asc', label: 'Date (ASC)', sort_column: 'created_at', sort_direction: 'ASC' },
        { value: 'created_at_desc', label: 'Date (DESC)', sort_column: 'created_at', sort_direction: 'DESC' },
        { value: 'date_expiration_asc', label: 'Date Expiration (ASC)', sort_column: 'date_expiration', sort_direction: 'ASC' },
        { value: 'date_expiration_desc', label: 'Date Expiration (DESC)', sort_column: 'date_expiration', sort_direction: 'DESC' },
    ];
    const showAdvancedFilters = ref(false);
    const loadingExportToExcel = ref(false);
    const loadingExportToPdf = ref(false);

    onMounted(() => {
        const startupDatas: any = route.meta.startup_infos;
        Object.assign(startupInfos, startupDatas);
        doListItems();
    });

    function getCartesStatutClassName(carte: any) {
        switch(carte.statut) {
            case 'delivery':
                return 'success';
            case 'delivering':
                return 'info';
            case 'inactif':
                return 'danger';
            default:
                return 'warning';
        }
    }

    async function doListItems() {
        try {
            loading.value = true;
            errorMessage.value = '';
            const queryParamsRaw = toRaw(queryParams);
            const queryString = new URLSearchParams(Object.fromEntries(Object.entries(queryParamsRaw).map(([k,v]) => [k, String(v)]))).toString();
            const httpResponse = await axios.get(`/backoffice/cartes/list?${queryString}`);
            itemsList.value = httpResponse.data.data;
            pagination.total = httpResponse.data.total;
            pagination.per_page = httpResponse.data.per_page;
            pagination.last_page = httpResponse.data.last_page;
        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            loading.value = false;
        }
    }

    function onPaginate(pageNumber: number) {
        queryParams.pageNumber = pageNumber;
        doListItems();
    }

    async function doDeleteItem(item: any) {
        const confirmation = await confirmationModalRef.value?.open("Cartes", "Voulez-vous vraiment supprimer l'élément sélectionné ?");
        if (confirmation) {
            try {
                loading.value = true;
                errorMessage.value = '';
                await axios.post(`/backoffice/cartes/destroy/${item.id}`, {});
                const index = itemsList.value.findIndex(i => i.id == item.id);
                if (index > -1) {
                    const itemsListCloned = itemsList.value;
                    itemsListCloned.splice(index, 1);
                    itemsList.value = itemsListCloned;
                }
            } catch(error: any) {
                errorMessage.value = error.message;
            } finally {
                loading.value = false;
            }
        }
    }

    function handleCheckAll(_event: any) {
        const checked = _event.target.checked;
        if (checked) {
            selectedItems.value = itemsList.value.map(i => i.id);
        } else {
            selectedItems.value = [];
        }
    }

    function onFiltersChanged() {
        const sortItem = SORT_OPTIONS.find(o => o.value == queryParams.sortOption);
        if (sortItem) {
            queryParams.sortColumn = sortItem.sort_column;
            queryParams.sortDir = sortItem.sort_direction;
        }
        doListItems();
    }

    function toggleAdvancedFilters() {
        showAdvancedFilters.value = !showAdvancedFilters.value;
    }

    async function doExportToExcelAction() {
        try {
            loadingExportToExcel.value = true;
            errorMessage.value = '';
            const queryParamsRaw = toRaw(queryParams);
            const queryString = new URLSearchParams(Object.fromEntries(Object.entries(queryParamsRaw).map(([k,v]) => [k, String(v)]))).toString();
            const response = await axios.get(`/backoffice/cartes/export_excel?${queryString}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'cartes.xlsx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            loadingExportToExcel.value = false;
        }
    }

    async function doExportToPdfAction() {
        try {
            loadingExportToPdf.value = true;
            errorMessage.value = '';
            const queryParamsRaw = toRaw(queryParams);
            const queryString = new URLSearchParams(Object.fromEntries(Object.entries(queryParamsRaw).map(([k,v]) => [k, String(v)]))).toString();
            const response = await axios.get(`/backoffice/cartes/export_pdf?${queryString}`, { responseType: 'blob' });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', 'cartes.pdf');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

        } catch (error: any) {
            errorMessage.value = error.message;
        } finally {
            loadingExportToPdf.value = false;
        }
    }

</script>
<template>
    <section class="container-full-app bg-grey-50">
        <div class="page-space-30"></div>

        <!--*** Widget *** -->
        <section class="s20">
            <div class="widget shadow-100 pb-2"><!-- widget shadow-100 fill -->
                <!--Card-->
                <div>
                    <div class="container-full-2 s20 my-5">
                        <div class="s7"><h4 class="font-semibold mt-4">Cartes <span class="text-sm text-grey-500">(recent)</span></h4></div>
                        <div class="s13 flex justify-content-right">
                            <router-link to="/app/cartes/form">
                                <button class="btn normal ml-4 justify-content-center btn-filtre">
                                    <img src="/icons/icon-add.svg" class="btn-icon" alt="" />
                                    <span>Créer</span>
                                </button>
                            </router-link>
                            <div class="form-field-floating default sm s4_mobile ml-3">
                                <select class="form-input-select" name="num_lot" id="num_lot" v-model="queryParams.num_lot" @change="onFiltersChanged">
                                    <option value="">Tous les lots</option>
                                    <option :value="option.num_lot" v-for="(option, index) in startupInfos.lots" :key="'filter_num_lot_' + index">Lot {{ option.num_lot }}</option>
                                </select>
                            </div>
                            <div class="form-field-floating default sm s4_mobile ml-3">
                                <select class="form-input-select" id="filters_sort" v-model="queryParams.sortOption" @change="onFiltersChanged">
                                    <option :value="option.value" v-for="(option, index) in SORT_OPTIONS" :key="'filter_sort_' + index">{{ option.label }}</option>
                                </select>
                            </div>
                            <button class="btn normal white2 s3_mobile ml-4 justify-content-center btn-filtre btn-filter" @click="toggleAdvancedFilters">
                                <img src="/icons/icon-filter.svg" class="btn-icon" alt="" />
                                <span>Options</span>
                            </button>
                        </div>
                        <div class="clear-both"></div>
                        <!--Options-->
                        <div class="custom-filter-set-area" v-if="showAdvancedFilters">
                            <div class="grid-filter6 mt-4 mb-0">
                                <div class="form-field-floating reactive sm">
                                    <input type="text" class="form-input" id="filters_search" v-model="queryParams.search" placeholder="" @keyup.enter="onFiltersChanged">
                                    <label for="filters_search">Recherche</label>
                                </div>
                                <div class="form-field-floating reactive sm">
                                    <input type="date" class="form-input" id="filters_borne_inf" v-model="queryParams.borneInf" placeholder="" @change="onFiltersChanged">
                                    <label for="filters_borne_inf">Date de création >=</label>
                                </div>
                                <div class="form-field-floating reactive sm">
                                    <input type="date" class="form-input" id="filters_borne_sup" v-model="queryParams.borneSup" placeholder="" @change="onFiltersChanged">
                                    <label for="filters_borne_sup">Date de création &lt;=</label>
                                </div>
                                <div class="form-field-floating sm">
                                    <select class="form-input-select" id="filter_banque" v-model="queryParams.banque_id" @change="onFiltersChanged">
                                        <option value="">Toutes les banques</option>
                                        <option :value="option.id" v-for="(option, index) in startupInfos.banques" :key="'filter_banque_' + index">{{ option.nom }}</option>
                                    </select>
                                    <label for="filter_banque">Banque <b class="color-danger">*</b></label>
                                </div>
                                <div class="form-field-floating sm">
                                    <select class="form-input-select" name="statut" id="statut" v-model="queryParams.statut" @change="onFiltersChanged">
                                        <option value="" selected>Toutes les cartes</option>
                                        <option value="pending">En attente</option>
                                        <option value="delivering">En cours de livraison</option>
                                        <option value="delivery">Actif</option>
                                        <option value="inactif">Inactif</option>
                                    </select>
                                    <label for="statut">Statut <b class="color-danger">*</b></label>
                                </div>
                                <button class="btn black normal justify-content-center mx-0 btn-filtre" :loading="loadingExportToExcel" @click="doExportToExcelAction">
                                    <img src="/icons/icon-excel.svg" class="btn-icon" alt="" />
                                    <span>Exporter Excel</span>
                                </button>
                                <button class="btn black normal justify-content-center mx-0 btn-filtre" :loading="loadingExportToPdf" @click="doExportToPdfAction">
                                    <img src="/icons/icon-pdf.svg" class="btn-icon" alt="" />
                                    <span>Exporter PDF</span>
                                </button>
                            </div>
                        </div>
                        <!---->
                    </div>
                    <table class="table separator skin1 sm selection"><!-- selection skin1 skin2 (bg1 bg2 bg3)  (skin3 rounded)  sm-->
                        <thead>
                            <tr>
                                <th>
                                    <label class="btn-circle" for="check-list1">
                                        <input type="checkbox" id="check_all" :checked="areAllItemsSelected" @change="handleCheckAll" />
                                    </label>
                                </th>
                                <th></th>
                                <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> Numéro</th>
                                <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Date expiration</th>
                                <th><img src="/icons/icon-filter-up.svg" class="size-xxs mt--1" alt="" /> N° de Lot</th>
                                <th><img src="/icons/icon-filter-down.svg" class="size-xxs mt--1" alt="" /> Banque</th>
                                <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Date de création</th>
                                <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Statut</th>
                                <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Affectée à</th>
                                <th><img src="/icons/icon-filter-none.svg" class="size-xxs mt--1" alt="" /> Solde</th>
                                <th class="flex-right">
                                    <button class="btn-ultra color2 tips">
                                        <div class="btn-show">
                                            <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                            <span>Actif</span>
                                        </div>
                                        <div class="btn-loading">
                                            <div class="loader-dots"></div>
                                        </div>
                                    </button>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-if="loading">
                                <td colspan="11">
                                    <div style="display: flex; justify-content: center" v-if="loading">
                                        <div class="loader dark"></div>
                                    </div>
                                </td>                            
                            </tr>
                            <template v-if="!loading">
                                <template v-for="(dataRow, index) in itemsList" :key="'row_' + index">
                                    <tr class="text-grey-700 over-effect over-effect-scale"><!-- over-effect active-->
                                        <td class="selection-item">
                                            <label class="btn-circle" :for="'item_check_' + index">
                                                <input type="checkbox" name="selectedItems[]" :id="'item_check_' + index" :value="dataRow.id" v-model="selectedItems" />
                                            </label>
                                        </td>
                                        <td><div class="square-lg px-3 py-3" style="background-color: rgb(var(--color-success-rgb), 0.2);"><img src="/icons/icon-menu-carte.svg" width="100%" /></div></td>
                                        <td class="font-semibold text-xl">{{ dataRow.numero }}</td>
                                        <td class="font-semibold text-xl text-center">{{ dataRow.expiration_annee + '/' + dataRow.expiration_mois }}</td>
                                        <td class="text-xl text-center">Lot {{ dataRow.num_lot ?? '-' }}</td>
                                        <td class="text-xl text-center">{{ dataRow.banque.nom }}</td>
                                        <td>{{ formatDate(dataRow.created_at) }}</td>
                                        <td><span :class="['tag-state', getCartesStatutClassName(dataRow), 'stroke']">{{ dataRow.statut_label }}</span></td>
                                        <td>{{ dataRow.user ? (dataRow.user.nom + ' ' + dataRow.user.prenom) : '-' }}</td>
                                        <td class="font-semibold text-center">{{ formatAmount(dataRow.solde) }}</td>
                                        <td class="flex-right">
                                            <button class="btn-ultra color2 tips">
                                                <div class="btn-show">
                                                    <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                                                    <span>Actif</span>
                                                </div>
                                                <div class="btn-loading">
                                                    <div class="loader-dots"></div>
                                                </div>
                                            </button>
                                        </td>
                                    </tr>
                                </template>
                            </template>
                        </tbody>
                    </table>
                    <!--Pagination-->
                    <div style="display: flex; flex-direction: row; justify-content: center;">
                        <custom-pagination v-model="queryParams.pageNumber" :total="pagination.total" :per-page="pagination.per_page" :last_page="pagination.last_page" @update:modelValue="onPaginate" />
                    </div>
                    <!---->
                </div>
                <div class="clear-both h-30"></div>
            </div>
        </section>
        
        <div class="clear-both h-30"></div>
    </section>
</template>